import React from 'react';
import { format } from 'date-fns';
import * as Styles from './AccountWithOneDraft.styles';
import useLocale from '@/hooks/useLocale';
import {
  Button, IconChat, IconHeart, IconMegaphone, IconSend, SelectField,
} from 'gatorcomponents';

const AccountWithOneDraft = ({
  handleOpenGator,
  options,
  SelectedAccountLabel,
  draft,
}) => {
  const { marketing: marketingLocale } = useLocale();
  const { multiAgentScreens: multiAgentScreensLocale } = marketingLocale;
  const { withOneDraft: withOneDraftLocale } = multiAgentScreensLocale;

  const strategy = draft && draft.post_strategy_selected ? draft.post_strategy_selected : null;
  const target = strategy && strategy.target ? strategy.target : null;
  const budget = strategy && strategy.budget ? strategy.budget : null;
  const cta = strategy && strategy.cta ? strategy.cta : null;

  const getTargetContent = () => {
    if (!target) return null;

    const ageMin = (target.age && Array.isArray(target.age) && target.age.length > 0) ? target.age[0] : 25;
    const ageMax = (target.age && Array.isArray(target.age) && target.age.length > 1) ? target.age[1] : 44;
    const locations = (target.location && target.location.data) ? target.location.data : [];
    const interests = target.interests || [];

    const locationNames = locations.map(loc => loc.name);
    const interestNames = interests.map(int => int.name);

    return withOneDraftLocale.targetContent({
      ageMin,
      ageMax,
      locations: locationNames,
      interests: interestNames,
    });
  };

  const getBudgetContent = () => {
    if (!budget) return null;

    const amount = budget.amount || 0;
    const frequency = budget.frequency || 'daily';
    const until = budget.until || '';

    const formattedAmount = amount % 1 === 0
      ? amount.toFixed(0)
      : amount.toFixed(2);

    const frequencies = {
      daily: withOneDraftLocale.budgetFrequencyDaily,
      weekly: withOneDraftLocale.budgetFrequencyWeekly,
      monthly: withOneDraftLocale.budgetFrequencyMonthly,
    };
    const frequencyText = frequencies[frequency] || withOneDraftLocale.budgetFrequencyDaily;

    const formattedDate = until ? format(new Date(until), 'dd/MM/yy') : '';

    return withOneDraftLocale.budgetContent({
      amount: formattedAmount,
      frequency: frequencyText,
      until: formattedDate,
    });
  };

  const getCtaContent = () => {
    if (!cta || (!cta.label && !cta.action)) {
      return withOneDraftLocale.ctaContent || 'N/A';
    }

    return cta.label || cta.action || 'N/A';
  };


  const handleReviewAndPublish = () => {
    handleOpenGator && handleOpenGator({
      message: withOneDraftLocale.reviewAndPublishMessage,
    });
  };

  const handleDiscard = () => {
    handleOpenGator && handleOpenGator({
      message: withOneDraftLocale.discardMessage,
    });
  };

  const ItemRow = ({
    id = 'target-row',
    label = 'title',
    content = 'content',
  }) => (
    <Styles.ItemRowContainer data-testid={id}>
      <Styles.ItemRowContent>
        <Styles.ItemRowLabel data-testid="target-title">
          {label}
        </Styles.ItemRowLabel>

        <Styles.ItemRowInfo>
          <Styles.ItemRowInfoText data-testid="target-info">
            {content}
          </Styles.ItemRowInfoText>
        </Styles.ItemRowInfo>
      </Styles.ItemRowContent>
    </Styles.ItemRowContainer>
  );

  return (
    <Styles.Container>
      <Styles.StyledContainer>
        <Styles.TitleRow>
          <Styles.Title>
            {withOneDraftLocale.firstTitle}
          </Styles.Title>


          <Styles.HeaderRightColumn>
            <Button
              iconLeft={<IconMegaphone color="highPure" />}
              label={withOneDraftLocale.myAds}
              size="large"
              notFillSVG
            />

            <Styles.SelectWrapper>
              <SelectField
                value={SelectedAccountLabel}
                options={options}
              />
            </Styles.SelectWrapper>
          </Styles.HeaderRightColumn>
        </Styles.TitleRow>

        <Styles.Content>
          <Styles.SecondTitle>
            {withOneDraftLocale.secondTitle}
          </Styles.SecondTitle>
          <Styles.Description>
            {withOneDraftLocale.description}
          </Styles.Description>

          <Styles.CardWrapper>
            <Styles.CardColumn>
              <Styles.PostCard>
                <Styles.CardImage src="https://placehold.co/600x400" />
                <Styles.PostDescriptionLabel>
                  {withOneDraftLocale.postDescriptionLabel}
                </Styles.PostDescriptionLabel>

                <Styles.Separator />

                <Styles.IconsRow>
                  <IconHeart color="lowLight" size="sm2" />
                  <IconChat color="lowLight" size="sm2" />
                  <IconSend color="lowLight" size="sm2" />
                </Styles.IconsRow>
              </Styles.PostCard>
            </Styles.CardColumn>

            <Styles.CardColumn>
              <Styles.PostDescriptionWrapper>
                <Styles.Tag>
                  {withOneDraftLocale.draftLabel}
                </Styles.Tag>

                <ItemRow
                  id="target"
                  label={withOneDraftLocale.targetLabel}
                  content={getTargetContent()}
                />

                <Styles.SeparatorWithoutMargin />

                <ItemRow
                  id="budget"
                  label={withOneDraftLocale.budgetLabel}
                  content={getBudgetContent()}
                />

                <Styles.SeparatorWithoutMargin />

                <ItemRow
                  id="cta"
                  label={withOneDraftLocale.ctaLabel}
                  content={getCtaContent()}
                />

                <Styles.ButtonRow>
                  <Button label={withOneDraftLocale.reviewAndPublishButton} size="large" onClick={handleReviewAndPublish} />
                  <Button label={withOneDraftLocale.discardButton} variant="tertiary" size="large" onClick={handleDiscard} />
                </Styles.ButtonRow>
              </Styles.PostDescriptionWrapper>
            </Styles.CardColumn>
          </Styles.CardWrapper>
        </Styles.Content>
      </Styles.StyledContainer>
    </Styles.Container>
  );
};

export default AccountWithOneDraft;
